import React, { useCallback, useEffect, useRef, useState } from "react";
import { getUrlParam } from "helpers/url-params";
import { useFilters } from "hooks/useStore";
import getCurrency from "helpers/get-currency";
import "./index.mod.scss";
import clsx from "clsx/lite";

const calculateAdjustedMinValue = (minValue, maxValue) => {
	let newMin = maxValue;
	while (newMin >= minValue) {
		newMin -= 100;
	}
	newMin += 100;
	return newMin.toFixed(2);
};

const PriceRangeSlider = ({ minValue, maxValue, handle, location }) => {
	const { collections, setPriceRange } = useFilters(["collections", "setPriceRange"]);
	const { priceRange = {} } = collections[handle] ?? {};

	const priceRangeParam = getUrlParam("price_range", location?.search)?.split("_");

	const minValRef = useRef(
		(priceRangeParam?.[0] >= minValue && priceRangeParam?.[0] <= maxValue)
			? priceRangeParam[0]
			: (priceRange?.min || minValue)
	);

	const maxValRef = useRef(
		(priceRangeParam?.[1] >= minValue && priceRangeParam?.[1] <= maxValue)
			? priceRangeParam[1]
			: (priceRange?.max || maxValue)
	);

	const range = useRef(null);
	const [tempRange, setTempRange] = useState({ min: minValRef.current, max: maxValRef.current });
	const adjustedMinValue = calculateAdjustedMinValue(minValue, maxValue);

	const customerCurrency = getCurrency()?.symbol;

	// Convert to percentage
	const getPercent = useCallback(
		(value) => Math.round(((value - minValue) / (maxValue - minValue)) * 100),
		[minValue, maxValue]);

	// Set width of the range to decrease from the left side
	useEffect(() => {
		const minPercent = getPercent(priceRange.min);
		const maxPercent = getPercent(maxValRef.current);

		if (range.current) {
			range.current.style.left = `${minPercent}%`;
			range.current.style.width = `${maxPercent - minPercent}%`;
			minValRef.current = priceRange.min;
		}
	}, [priceRange.min]);

	// Set width of the range to decrease from the right side
	useEffect(() => {
		const minPercent = getPercent(minValRef.current);
		const maxPercent = getPercent(priceRange.max);

		if (range.current) {
			range.current.style.width = `${maxPercent - minPercent}%`;
			maxValRef.current = priceRange.max;
		}
	}, [priceRange.max]);

	const handleMinChange = (event) => {
		const value = Math.min(Number(event.target.value), priceRange.max - 100).toFixed(2);
		if (value < minValue) return;

		setPriceRange(handle, { min: value, max: maxValRef.current });
		setTempRange((prev) => ({ ...prev, min: value }));
	};

	const handleMaxChange = (event) => {
		const value = Math.max(Number(event.target.value), priceRange?.min + 100).toFixed(2);
		if (value - 100 < minValRef.current || value > maxValue) return;

		setPriceRange(handle, { min: minValRef.current, max: value });
		setTempRange((prev) => ({ ...prev, max: value }));
	};

	const handleTempMinChange = (event) => {
		const value = event.target.value;
		const maxMinus100 = +priceRange.max - 100;

		// If value is between min and maxMinus100
		if (+value >= minValue && +value <= maxMinus100) {
			setPriceRange(handle, { max: maxValRef.current, min: Number(value).toFixed(2) });
			setTempRange((prev) => ({ ...prev, min: value }));
			return;
		}

		// If value is less than minValue
		if (+value > maxMinus100) {
			setPriceRange(handle, { min: Number(maxMinus100).toFixed(2), max: maxValRef.current });
			setTempRange((prev) => ({ ...prev, min: Number(maxMinus100).toFixed(2) }));
			return;
		}

		setTempRange((prev) => ({ ...prev, min: value }));
	};

	const handleTempMaxChange = (event) => {
		const value = event.target.value;
		const minPlus100 = +priceRange.min + 100;

		// If value is between minPlus100 and max
		if (+value >= +minPlus100 && +value <= maxValue) {
			setPriceRange(handle, { min: minValRef.current, max: Number(value).toFixed(2) });
			setTempRange((prev) => ({ ...prev, max: value }));
			return;
		}

		// If value is greater than maxValue
		if (+value > maxValue) {
			setPriceRange(handle, { min: minValRef.current, max: maxValue });
			setTempRange((prev) => ({ ...prev, max: maxValue }));
			return;
		}

		setTempRange((prev) => ({ ...prev, max: value }));
	};

	const onBlurMin = () => {
		// If value is less than minValue
		if (tempRange.min < minValue) {
			setPriceRange(handle, { min: minValue, max: maxValRef.current });
			setTempRange((prev) => ({ ...prev, min: minValue }));
			return;
		}

		setTempRange((prev) => ({ ...prev, min: Number(tempRange.min).toFixed(2) }));
	};

	const onBlurMax = () => {
		const minPlus100 = +priceRange.min + 100;

		// If value is smaller than minPlus100
		if (tempRange.max < minPlus100) {
			setPriceRange(handle, { min: priceRange.min, max: Number(minPlus100).toFixed(2) });
			setTempRange((prev) => ({ ...prev, max: Number(minPlus100).toFixed(2) }));
			return;
		}

		setTempRange((prev) => ({ ...prev, max: Number(tempRange.max).toFixed(2) }));

	};

	return (
		<div className="flex flex-col gap-5 h-40 border-t max-w-full lg:w-10/12 py-5">
			<h4 className="font-bold">Shop by Price</h4>
			<div>
				<label htmlFor="min" className="sr-only">Min value</label>
				<input type="range" min={minValue} max={maxValue} value={priceRange.min || minValue} step={100}
					onChange={handleMinChange}
					className={clsx("thumb thumb-left", priceRange.min > maxValue - 100 ? "!z-[5]" : "z-[3]")} id="min"
				/>
				<label htmlFor="max" className="sr-only">Max value</label>
				<input
					type="range" min={adjustedMinValue} max={maxValue} value={priceRange.max || maxValue} step={100}
					className="thumb thumb-right" onChange={handleMaxChange} id="max"
				/>
				<div className="slider">
					<div className="slider-track" />
					<div ref={range} className="slider-range" />
					<div className="flex justify-between items-center pr-2">
						<div className="flex items-center">
							<span className="pr-1 mt-6 font-semibold">{customerCurrency}</span>
							<label htmlFor="min" className="sr-only">Min value</label>
							<input
								type="number"
								min={0} max={maxValue}
								value={tempRange.min}
								onBlur={onBlurMin}
								onKeyDown={(e) => e.key === "Enter" && onBlurMin()}
								onChange={handleTempMinChange}
								step={100}
								name="min"
								aria-label="min value"
								className="slider-left-value" />
						</div>
						<span className="mt-6 px-1">-</span>
						<div className="flex items-center">
							<span className="pr-1 mt-6 font-semibold">{customerCurrency}</span>
							<label htmlFor="max" className="sr-only">Max</label>
							<input
								type="number"
								min={0} max={maxValue}
								value={tempRange.max}
								onBlur={onBlurMax}
								onKeyDown={(e) => e.key === "Enter" && onBlurMax()}
								onChange={handleTempMaxChange}
								step={100}
								name="max"
								aria-label="max value"
								className="slider-right-value" />
						</div>
					</div>
				</div>
			</div>
		</div>

	);
};

export default PriceRangeSlider;
